'use client';

import { useMemo, useState, useEffect } from 'react';
import { createAppAdjustLink } from './helpers/createAppAdjustLink';
import { getAdgroupId } from './helpers/getAdgroupId';
import { getLabel } from './helpers/getLabel';
import { getPageName } from './helpers/getPageName';
import { getParametersInUrl } from './helpers/getParametersInUrl';
import { isPlaceholderPresent } from './helpers/isPlaceholderPresent';
import { replaceAppAdjustLinkPlaceholder } from './helpers/replaceAppAdjustLinkPlaceholder';
import { REGEX_CONDITION, URL_PARAMS_KEY } from '../../constants';
import { isBrowser, safeEncodeUrlParams, ENCODING_WHITELIST_CHARS, PixumEnvUtils } from 'utils';
import { useLocation } from '../useLocation';
import { addJentisSessionIdAndUserIdToAdjustLink } from './helpers/addJentisSessionIdAndUserIdToAdjustLink';

/**
 * Custom hook `useAppAdjustLink` generates a final Adjust link based on the provided initial link and creative ID.
 * It utilizes various helper functions and user/store data to dynamically construct the link with relevant parameters.
 *
 * @param  link - The initial Adjust link to be processed.
 * @param  creative_id - The creative ID to be included in the Adjust link.
 * @returns The final Adjust link with placeholders replaced and additional parameters added, or undefined if no window object exists.
 */
export function useAppAdjustLink(link = '', creative_id = '') {
  // Forces a re-render once PIXUM_ENV.USER is extended with session data so jentisUserId and
  // prefInfo are read with their real values instead of the empty defaults present at mount.
  const [, setEnvExtended] = useState(false);

  useEffect(() => PixumEnvUtils.onceExtended(() => setEnvExtended(true)), []);

  const location = useLocation();

  const { pathname, valuesInUrl } = useMemo(() => {
    const urlParameters = location.search.trim();

    return {
      pathname: location.pathname.trim(),
      valuesInUrl: getParametersInUrl(urlParameters),
    };
  }, [location]);

  const user = PixumEnvUtils.get('USER');
  const prefInfo = user?.prefInfo ?? {};

  const userPref = useMemo(() => {
    const prefDetails = prefInfo;
    const prefKeys = Object.keys(prefDetails);

    return prefKeys.length > 0 ? prefDetails.prefFull : '';
  }, [prefInfo]) as string;

  const adGroupId = useMemo(() => getAdgroupId(userPref), [userPref]) as string;

  const campaignId = useMemo(() => getPageName(pathname, 2), [pathname]);

  const labelValue = useMemo(() => {
    const pathNamePrepared = pathname
      .replace(REGEX_CONDITION.LEADING_AND_TRAILING_SLASHES, '')
      .replace(REGEX_CONDITION.SLASHES, '_');

    return getLabel(valuesInUrl, pathNamePrepared, userPref);
  }, [pathname, userPref]);

  const parametersMap = useMemo(
    () => ({
      ...valuesInUrl,
      adgroup_id: adGroupId,
      campaign_id: campaignId,
      creative_id,
      UserPref: userPref,
      label: labelValue,
    }),
    [valuesInUrl, adGroupId, campaignId, userPref, labelValue, creative_id],
  );

  return useMemo(() => {
    // Browser check is deferred to here so all hook calls above are always executed,
    // satisfying React's Rules of Hooks (hooks must not be called after early returns).
    if (!isBrowser()) return link;

    if (!link) return;

    const jentisUserId = user?.jentisUserId;
    const jentisSessionId = user?.jentisSessionId;

    let finalLink = addJentisSessionIdAndUserIdToAdjustLink(link, jentisSessionId, jentisUserId);

    const hasPlaceholder = isPlaceholderPresent(finalLink);
    const hasDeeplink = finalLink.includes(URL_PARAMS_KEY.DEEP_LINK);

    if (hasPlaceholder) {
      finalLink = replaceAppAdjustLinkPlaceholder(finalLink, parametersMap);

      return safeEncodeUrlParams(finalLink, ENCODING_WHITELIST_CHARS);
    } else if (!hasPlaceholder && hasDeeplink) {
      const { adgroup_id, campaign_id, creative_id, label } = parametersMap;
      const lpParamsMap = {
        adgroup: adgroup_id,
        campaign: campaign_id,
        creative: creative_id,
        label,
      };

      finalLink = createAppAdjustLink(finalLink, lpParamsMap);

      return safeEncodeUrlParams(finalLink, ENCODING_WHITELIST_CHARS);
    } else {
      return safeEncodeUrlParams(finalLink, ENCODING_WHITELIST_CHARS);
    }
  }, [link, creative_id, parametersMap, user]);
}
